import React from 'react';
import { Button } from '../../../design-system';
import {
  AnalysisMethod,
  BatchProgress,
  BatchStatus,
  METHOD_LABELS,
} from '../types';
import styles from '../MultiAnalysisPage.module.css';

type Props = {
  batchStatus: BatchStatus;
  progress: BatchProgress;
  checkedCount: number;
  failedCount: number;
  savableCount: number;
  /** Mean fit metric of the active methodology (RMSE for Liao, R² for BC). */
  aggregateFit: number | null;
  /** Active batch methodology — drives labels and the fit metric name. */
  method: AnalysisMethod;
  /** Count of cards currently visible under the active view filters. */
  visibleCount: number;
  onCheckAll: () => void;
  onCheckNone: () => void;
  onCheckVisible: () => void;
  onRun: () => void;
  onCancel: () => void;
  onRerunFailed: () => void;
  onSave: () => void;
};

/**
 * Shared controls bar — the analogue of IP's bottom calibration bar:
 * batch Liao inputs, Select All/None, aggregate fit error, run/cancel
 * actions and progress.
 */
const BatchControlsBar = ({
  batchStatus,
  progress,
  checkedCount,
  failedCount,
  savableCount,
  aggregateFit,
  method,
  visibleCount,
  onCheckAll,
  onCheckNone,
  onCheckVisible,
  onRun,
  onCancel,
  onRerunFailed,
  onSave,
}: Props) => {
  const running = batchStatus === 'running';

  return (
    <div className={styles.controlsBar}>
      <div className={styles.controlsGroup}>
        <Button variant="secondary" size="sm" onClick={onCheckAll} disabled={running}>
          Select All
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={onCheckVisible}
          disabled={running || visibleCount === 0}
        >
          Select Visible ({visibleCount})
        </Button>
        <Button variant="secondary" size="sm" onClick={onCheckNone} disabled={running}>
          Select None
        </Button>
      </div>

      <div className={styles.controlsGroup}>
        <span className={styles.controlsLabel}>
          {method === 'lambda' ? 'Mean R²' : 'Mean RMSE'}{' '}
          {aggregateFit != null ? aggregateFit.toFixed(4) : '—'}
        </span>
      </div>

      <div className={styles.controlsSpacer} />

      {running ? (
        <div className={styles.controlsGroup}>
          <span className={styles.progressText}>
            Running {progress.done}/{progress.total}…
          </span>
          <div
            className={styles.progressTrack}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={progress.total}
            aria-valuenow={progress.done}
          >
            <div
              className={styles.progressFill}
              style={{
                width: `${
                  progress.total > 0
                    ? Math.round((progress.done / progress.total) * 100)
                    : 0
                }%`,
              }}
            />
          </div>
          <Button variant="secondary" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      ) : (
        <div className={styles.controlsGroup}>
          {failedCount > 0 ? (
            <Button variant="secondary" size="sm" onClick={onRerunFailed}>
              Rerun failed ({failedCount})
            </Button>
          ) : null}
          <Button
            variant="secondary"
            onClick={onRun}
            disabled={checkedCount === 0}
          >
            Preview {METHOD_LABELS[method]} ({checkedCount})
          </Button>
          <Button
            variant="primary"
            onClick={onSave}
            disabled={savableCount === 0}
          >
            Save reviewed ({savableCount})
          </Button>
        </div>
      )}
    </div>
  );
};

export default BatchControlsBar;
